'use client';

import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface GuideCategoria {
  id: string;
  nome: string;
  descrizione: string;
  icona: string;
  colore: string;
  categoria: string;
  ordine: number;
  guide_count?: number;
}

const CATEGORIA_HREF: Record<string, string> = {
  stampanti_normali: '/guide/stampanti',
  stampanti_etichette: '/guide/stampanti-etichette',
  dicom: '/guide/dicom',
  analizzatori: '/guide/analizzatori',
  barcode_rfid: '/guide/barcode-rfid',
  centrifughe_strumenti: '/guide/centrifughe',
  scanner_archiviazione: '/guide/scanner',
  nas_server: '/guide/nas-server',
  ups_continuita: '/guide/ups',
  thin_client_terminali: '/guide/thin-client',
  monitor_display: '/guide/monitor-display',
  vpn_client: '/guide/vpn',
  stampanti_ricette: '/guide/stampanti-ricette',
  tessera_sanitaria: '/guide/tessera-sanitaria',
};

const COLORE_MAP: Record<string, { text: string; bg: string; border: string }> = {
  sky: { text: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/30' },
  amber: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  purple: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  rose: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
  orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
  teal: { text: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/30' },
  indigo: { text: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30' },
  yellow: { text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  violet: { text: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/30' },
  lime: { text: 'text-lime-400', bg: 'bg-lime-500/10', border: 'border-lime-500/30' },
  pink: { text: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30' },
  fuchsia: { text: 'text-fuchsia-400', bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/30' },
};

export default function GuidePage() {
  const [categorie, setCategorie] = useState<GuideCategoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategorie = async () => {
      try {
        const supabase = createClient();
        const { data, error: err } = await supabase
          .from('guide_categorie')
          .select('*')
          .eq('is_active', true)
          .order('ordine', { ascending: true });

        if (err) {
          setError('Errore nel caricamento delle categorie');
          return;
        }
        setCategorie(data || []);
      } catch {
        setError('Errore di connessione');
      } finally {
        setLoading(false);
      }
    };
    fetchCategorie();
  }, []);

  return (
    <AppLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center">
              <Icon name="BookOpenIcon" size={26} className="text-sky-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Guide e Configurazione Dispositivi</h1>
              <p className="text-lg text-secondary-color mt-0.5">Scegli una categoria per vedere le guide passo passo</p>
            </div>
          </div>
          <div className="mt-4 p-4 rounded-xl bg-sky-500/5 border border-sky-500/20">
            <p className="text-base text-secondary-color leading-relaxed">
              💡 <strong className="text-white">Come usare questa sezione:</strong> Clicca su una delle categorie qui sotto per aprire le guide complete.
              Ogni guida ha istruzioni dettagliate con valutazione difficoltà e storico configurazioni.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-secondary-color text-sm">
            {loading ? 'Caricamento...' : `${categorie.length} categorie disponibili`}
          </p>
          <Link
            href="/storico-configurazioni"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-surface border border-border text-secondary-color hover:text-white hover:border-sky-500/50 transition-all text-sm font-semibold"
          >
            <Icon name="ClockIcon" size={16} />
            Storico Configurazioni
          </Link>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-48 rounded-2xl bg-surface border border-border animate-pulse" />
            ))}
          </div>
        )}

        {/* Guide Cards Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {categorie.map((cat) => {
              const href = CATEGORIA_HREF[cat.categoria] || `/guide/${cat.categoria}`;
              const colori = COLORE_MAP[cat.colore] || COLORE_MAP['sky'];
              return (
                <Link
                  key={cat.id}
                  href={href}
                  className={`block p-6 rounded-2xl border ${colori.bg} ${colori.border} hover:scale-[1.02] transition-all duration-200 group cursor-pointer`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-xl ${colori.bg} border ${colori.border} flex items-center justify-center flex-shrink-0`}>
                      <Icon name={cat.icona as any} size={30} className={colori.text} />
                    </div>
                    <h2 className={`text-xl font-bold ${colori.text}`}>{cat.nome}</h2>
                  </div>
                  <p className="text-base text-secondary-color mb-5 leading-relaxed">{cat.descrizione}</p>
                  <div className={`flex items-center gap-2 ${colori.text} font-semibold text-base group-hover:gap-3 transition-all`}>
                    <span>Apri le guide</span>
                    <Icon name="ArrowRightIcon" size={18} className={colori.text} />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Help Banner */}
        <div className="mt-8 p-5 rounded-2xl bg-surface border border-border">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <Icon name="PhoneIcon" size={24} className="text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-1">Hai bisogno di aiuto?</h3>
              <p className="text-base text-secondary-color leading-relaxed">
                Se non riesci a risolvere il problema da solo, chiama il supporto tecnico interno oppure contatta il responsabile IT del laboratorio.
              </p>
              <p className="text-base font-semibold text-amber-400 mt-2">📞 Interno: 200 — IT Supporto</p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
